import { useRegisterSW } from "virtual:pwa-register/react";

export interface PWARegisterState {
  needRefresh: boolean;
  offlineReady: boolean;
  updateServiceWorker: (reloadPage?: boolean) => Promise<void>;
  setNeedRefresh: (value: boolean) => void;
  setOfflineReady: (value: boolean) => void;
}

export function canUsePWA(): boolean {
  return typeof window !== "undefined" && "serviceWorker" in navigator;
}

/**
 * Called once in main.tsx before the React tree mounts.
 * Returns false when the environment does not support service workers.
 */
export function registerSW(): boolean {
  return canUsePWA();
}

/**
 * React hook that registers the service worker and exposes its state.
 * Use inside a component mounted in the provider tree.
 *
 * – onOfflineReady: dispatches a CustomEvent("pwa:offline-ready") that the
 *   app's toast system can listen to independently.
 * – onNeedRefresh:  sets needRefresh=true so <UpdateAvailableBanner> renders.
 */
export function usePWARegister(): PWARegisterState {
  const {
    offlineReady: [offlineReady, setOfflineReady],
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onOfflineReady() {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("pwa:offline-ready"));
      }
    },
    onNeedRefresh() {
      // The banner renders from the adapter state and updates only after
      // explicit user action via updateServiceWorker(true).
    },
  });

  return {
    needRefresh,
    offlineReady,
    updateServiceWorker,
    setNeedRefresh,
    setOfflineReady,
  };
}
